import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  Box,
  Stack,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  Badge,
  CircularProgress,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { purple } from "@mui/material/colors";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import ReplayIcon from "@mui/icons-material/Replay";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";

// Totals table
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(purple[500]),
  backgroundColor: purple[500],
  boxShadow: "2px 2px 6px #404142",
  "&:hover": { backgroundColor: purple[700] },
}));

/** Compact multiline TextField settings */
const compactTfSx = {
  "& .MuiInputBase-input": {
    padding: "6px 10px",
    fontSize: 13,
    lineHeight: 1.25,
  },
  "& .MuiInputLabel-root": { fontSize: 12 },
};

const smallIconBtnSx = { p: 0.35, "& svg": { fontSize: 18 } };

const safeNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const safeText = (v) => (v === null || v === undefined ? "" : String(v));

const normalizeHeaderDate = (v) => {
  try {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return dayjs(d);
  } catch (e) {}
  return dayjs();
};

const deriveItemcode = (row) => {
  const direct = safeText(row?.itemcode).trim();
  if (direct) return direct;
  const partno = safeText(row?.partno || row?.partdetails || row?.partdetails1).trim();
  if (!partno) return "";
  const idx = partno.indexOf("-");
  return idx > 0 ? partno.slice(0, idx).trim() : partno.trim();
};

const deriveItemname = (row) => {
  const direct = safeText(row?.itemname).trim();
  if (direct) return direct;
  const partno = safeText(row?.partno || row?.partdetails || row?.partdetails1).trim();
  if (!partno) return "";
  const idx = partno.indexOf("-");
  return idx >= 0 ? partno.slice(idx + 1).trim() : "";
};

const StkTransferByJson = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const apiRef = useGridApiRef();

  // =======================
  // MODE
  // =======================
  const [mode, setMode] = useState(1); // 1=New, 3=View
  const isViewMode = mode === 3;
  const allmodes = [
    { value: 1, label: "New" },
    { value: 3, label: "View" },
  ];

  // ========= HEADER STATE =========
  const [orderdate, setorderdate] = useState(dayjs());
  const [refdocno, setRefDocno] = useState("");

  // ========= FROM =========
  const [frombranchcompany, setFromBranchCompany] = useState("");
  const [frombranchname, setFromBranchName] = useState("");
  const [frombranchaddr1, setFromBranchAddr1] = useState("");
  const [frombranchaddr2, setFromBranchAddr2] = useState("");
  const [frombranchaddr3, setFromBranchAddr3] = useState("");

  // ========= TO =========
  const [tobranchcompany, setToBranchCompany] = useState("");
  const [tobranchname, setToBranchName] = useState("");
  const [tobranchaddr1, setToBranchAddr1] = useState("");
  const [tobranchaddr2, setToBranchAddr2] = useState("");
  const [tobranchaddr3, setToBranchAddr3] = useState("");

  // originals for reset
  const [fromOriginal, setFromOriginal] = useState(null);
  const [toOriginal, setToOriginal] = useState(null);

  // =========================
  // GRID
  // =========================
  const [rows, setrows] = useState([]);

  const handleDeleteClick = (id) => () => {
    if (isViewMode) return;
    setrows((prev) => prev.filter((r) => String(r._rowKey) !== String(id)));
  };

  const recalcRow = (row) => {
    const saleqty = safeNum(row.saleqty, 0);
    const price = safeNum(row.price, 0);
    const gstpers = safeNum(row.gstpers, 0);
    const value = Number((saleqty * price).toFixed(2));
    const gstamt = Number(((value * gstpers) / 100).toFixed(2));
    const netamt = Number((value + gstamt).toFixed(2));
    return { ...row, value, gstamt, netamt };
  };

  // =========================
  // RACK OPTIONS
  // =========================
  const [rackOptions, setRackOptions] = useState([]);

  const loadRacks = async () => {
    try {
      const r = await fetch("/stk/transfer/racks", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const j = await r.json().catch(() => ({}));
      if (j?.type === "success" && Array.isArray(j.rows)) {
        const normalized = j.rows.map((x) => ({
          rackid: x.rackid ?? x.id ?? x.rack_id ?? 0,
          rackname: x.rackname ?? x.name ?? x.rack_name ?? "",
        }));
        setRackOptions(normalized.filter((x) => x.rackid && x.rackname));
      } else setRackOptions([]);
    } catch (e) {
      console.log(e);
      setRackOptions([]);
    }
  };

  useEffect(() => {
    if (userToken) loadRacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  // ✅ Rack editor — binds properly to DataGrid + state
  const RackEditCell = (params) => {
    const { id, row } = params;
    const currentRackid = safeNum(row?.rackid, 0);
    const current =
      rackOptions.find((r) => String(r.rackid) === String(currentRackid)) || null;

    return (
      <Autocomplete
        options={rackOptions}
        value={current}
        getOptionLabel={(o) => o?.rackname || ""}
        isOptionEqualToValue={(o, v) => String(o?.rackid) === String(v?.rackid)}
        onChange={async (_, newVal) => {
          const newRackid = newVal?.rackid ? Number(newVal.rackid) : 0;
          const newRackname = newVal?.rackname ? String(newVal.rackname) : "NA";

          // 1) Update React state (for Save payload)
          setrows((prev) =>
            prev.map((r) =>
              String(r._rowKey) === String(id)
                ? { ...r, rackid: newRackid, rackname: newRackname }
                : r
            )
          );

          // 2) Update DataGrid edit value
          try {
            await apiRef.current.setEditCellValue({
              id,
              field: "rackname",
              value: newRackname,
            });
          } catch (e) {}

          // 3) Update row model
          try {
            apiRef.current.updateRows([{ id, rackid: newRackid, rackname: newRackname }]);
          } catch (e) {}

          // 4) Close edit
          try {
            apiRef.current.stopCellEditMode({ id, field: "rackname" });
          } catch (e) {}
        }}
        renderInput={(p) => <TextField {...p} size="small" label="Rack" />}
        fullWidth
        sx={{ minWidth: 160 }}
      />
    );
  };

  // =========================
  // TOTALS
  // =========================
  const totals = useMemo(() => {
    const totvalue = rows.reduce((sum, r) => sum + safeNum(r.value, 0), 0);
    const totgstamt = rows.reduce((sum, r) => sum + safeNum(r.gstamt, 0), 0);
    const totnetamt = rows.reduce((sum, r) => sum + safeNum(r.netamt, 0), 0);
    return {
      totvalue: totvalue.toFixed(2),
      totgstamt: totgstamt.toFixed(2),
      totnetamt: totnetamt.toFixed(2),
    };
  }, [rows]);

  // =========================
  // SAVE
  // =========================
  const [saving, setSaving] = useState(false);

  const buildInsertPayload = () => {
    const badItem = rows.find((r) => safeNum(r.itemid, 0) <= 0);
    if (badItem) {
      throw new Error(
        `Item not found for itemcode: ${safeText(badItem.itemcode) || "-"} (cannot save)`
      );
    }

    const itemdata = rows.map((r) => ({
      itemid: safeNum(r.itemid, 0),
      itemcode: safeText(r.itemcode),
      itemname: safeText(r.itemname),
      saleqty: safeNum(r.saleqty, 0),
      value: safeNum(r.value, 0),
      gstamt: safeNum(r.gstamt, 0),
      netamt: safeNum(r.netamt, 0),
      mrp: safeNum(r.mrp, 0),
      rackid: safeNum(r.rackid, 0),
      rackname: safeText(r.rackname) || "NA",
    }));

    return {
      itemdata,
      tobranch: safeText(tobranchname),
      documentdate: orderdate?.toDate ? orderdate.toDate() : new Date(),
      refdocno: safeText(refdocno),
      totgross: safeNum(totals.totvalue, 0),
      totgst: safeNum(totals.totgstamt, 0),
      totnet: safeNum(totals.totnetamt, 0),
      frombranchcompany,
      frombranchname,
      frombranchaddr1,
      frombranchaddr2,
      frombranchaddr3,
      tobranchcompany,
      tobranchname,
      tobranchaddr1,
      tobranchaddr2,
      tobranchaddr3,
    };
  };

  const saveTransfer = async () => {
    try {
      if (isViewMode) return;
      if (!rows.length) return toast.error("No rows to save");
      const payload = buildInsertPayload();

      setSaving(true);
      const r = await fetch("/stk/transfer/insert", {
        method: "POST",
        headers: { "Content-Type": "application/json", sptoken: userToken },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.type === "error") throw new Error(j?.message || "Save failed");

      toast.success(j?.message || "Saved!");
      setrows([]);
      setPickedFileName("");
      setParsedJson(null);
      setMismatchItems([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      console.log(e);
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // COLUMNS
  // =========================
  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerClassName: "super-app-theme--header",
        headerName: "Del",
        width: 70,
        getActions: ({ id }) =>
          isViewMode
            ? []
            : [
                <GridActionsCellItem
                  key="del"
                  icon={<DeleteIcon />}
                  label="Delete"
                  onClick={handleDeleteClick(id)}
                  color="inherit"
                />,
              ],
      },
      { field: "serial", headerName: "Srl", headerClassName: "super-app-theme--header", width: 60, type: "number" },
      { field: "itemcode", headerName: "Item Code", headerClassName: "super-app-theme--header", width: 130 },
      { field: "itemname", headerName: "Item Name", headerClassName: "super-app-theme--header", width: 220 },
      //{ field: "partno", headerName: "Partno", headerClassName: "super-app-theme--header", width: 260 },
      { field: "stkqty", headerName: "Stk Qty", headerClassName: "super-app-theme--header", width: 90, type: "number" },

      {
        field: "rackname",
        headerName: "Rack",
        headerClassName: "super-app-theme--header",
        width: 140,
        editable: !isViewMode,
        renderEditCell: RackEditCell,
        valueGetter: (params) => {
          const rid = params?.row?.rackid;
          const found = rackOptions.find((r) => String(r.rackid) === String(rid));
          return found?.rackname || params?.row?.rackname || "NA";
        },
        renderCell: (params) => safeText(params.value || "NA"),
      },

      { field: "saleqty", headerName: "Transfer Qty", headerClassName: "super-app-theme--header", width: 120, type: "number" },
      { field: "mrp", headerName: "MRP", headerClassName: "super-app-theme--header", width: 90, type: "number" },
      { field: "price", headerName: "Price", headerClassName: "super-app-theme--header", width: 90, type: "number" },
      { field: "value", headerName: "Value", headerClassName: "super-app-theme--header", width: 100, type: "number" },
      { field: "gstpers", headerName: "GST %", headerClassName: "super-app-theme--header", width: 80, type: "number" },
      { field: "gstamt", headerName: "GST Amt", headerClassName: "super-app-theme--header", width: 110, type: "number" },
      { field: "netamt", headerName: "Net Amt", headerClassName: "super-app-theme--header", width: 120, type: "number" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isViewMode, rackOptions]
  );

  // =========================
  // BRANCH CARD
  // =========================
  const ViewRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <Typography variant="caption" sx={{ minWidth: 92, color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: 13, lineHeight: 1.25, whiteSpace: "pre-wrap" }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const BranchCardViewOnly = ({ title, onReset, company, branch, addr1, addr2, addr3 }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Tooltip title="Reset">
          <span>
            <IconButton size="small" sx={smallIconBtnSx} onClick={onReset} disabled={isViewMode}>
              <ReplayIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Divider sx={{ my: 0.8 }} />
      <Stack spacing={0.6}>
        <ViewRow label="Company" value={company} />
        <ViewRow label="Branch" value={branch} />
        <ViewRow label="Addr 1" value={addr1} />
        <ViewRow label="Addr 2" value={addr2} />
        <ViewRow label="Addr 3" value={addr3} />
      </Stack>
    </Paper>
  );

  const applyFrom = (obj) => {
    setFromBranchCompany(safeText(obj?.frombranchcompany ?? obj?.companyname));
    setFromBranchName(safeText(obj?.frombranchname ?? obj?.branchname));
    setFromBranchAddr1(safeText(obj?.frombranchaddr1 ?? obj?.einvadd1));
    setFromBranchAddr2(safeText(obj?.frombranchaddr2 ?? obj?.einvadd2));
    setFromBranchAddr3(safeText(obj?.frombranchaddr3 ?? obj?.einvadd3));
  };

  const applyTo = (obj) => {
    setToBranchCompany(safeText(obj?.tobranchcompany ?? obj?.companyname));
    setToBranchName(safeText(obj?.tobranchname ?? obj?.branchname));
    setToBranchAddr1(safeText(obj?.tobranchaddr1 ?? obj?.einvadd1));
    setToBranchAddr2(safeText(obj?.tobranchaddr2 ?? obj?.einvadd2));
    setToBranchAddr3(safeText(obj?.tobranchaddr3 ?? obj?.einvadd3));
  };

  const resetFrom = () => {
    if (!fromOriginal) return;
    applyFrom(fromOriginal);
    toast.info("FROM reset");
  };
  const resetTo = () => {
    if (!toOriginal) return;
    applyTo(toOriginal);
    toast.info("TO reset");
  };

  // =========================
  // JSON UPLOAD + VERIFY FLOW
  // =========================
  const fileInputRef = useRef(null);
  const [pickedFileName, setPickedFileName] = useState("");
  const [parsedJson, setParsedJson] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState({ docno: "", items: 0, from: "", to: "" });

  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [mismatchItems, setMismatchItems] = useState([]);
  const openMismatch = () => setMismatchOpen(true);
  const closeMismatch = () => setMismatchOpen(false);

  const parseAndStageJson = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) return toast.error("Please upload a .json file");

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data || typeof data !== "object") return toast.error("Invalid JSON object");
      if (!data.header || typeof data.header !== "object") return toast.error("Missing header in JSON");
      if (!Array.isArray(data.detail)) return toast.error("Missing detail array in JSON");

      setPickedFileName(file.name);
      setParsedJson(data);
      setMismatchItems([]);

      const h = data.header || {};
      setConfirmSummary({
        docno: safeText(h.docno || h.doc_no || h.docnumber || ""),
        items: data.detail.length,
        from: safeText(h.frombranchname || h.frombranchcompany || ""),
        to: safeText(h.tobranchname || h.tobranchcompany || ""),
      });

      setRefDocno(safeText(h.refdocno));
      toast.success("JSON loaded. Click VERIFY to verify & bind.");
    } catch (e) {
      console.error(e);
      toast.error("Invalid JSON file");
    }
  };

  const onPickFile = (e) => parseAndStageJson(e.target.files?.[0]);

  const openVerifyConfirm = () => {
    if (!parsedJson) return toast.error("Upload a JSON first");
    setConfirmOpen(true);
  };

  // ✅✅ FIXED: matches backend response shape
  const verifyWithBackend = async (data) => {
    const detail = Array.isArray(data?.detail) ? data.detail : [];

    const r = await fetch("/stk/transfer/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json", sptoken: userToken },
      body: JSON.stringify({ detail }),
    });

    const j = await r.json().catch(() => ({}));

    // backend returns {type,message,missingItems,items}
    const items = Array.isArray(j?.items) ? j.items : [];
    const missingItems = Array.isArray(j?.missingItems) ? j.missingItems : [];

    return {
      ok: r.ok,
      type: j?.type || "error",
      message: j?.message || "Verify failed",
      items,
      missingItems,
    };
  };

  const bindHeader = (h) => {
    setorderdate(normalizeHeaderDate(h.docdate || h.documentdate || h.date));
    setRefDocno((prev) => prev || safeText(h.refdocno));
    applyFrom(h);
    applyTo(h);
    setFromOriginal(h);
    setToOriginal(h);
  };

  // ✅✅ FIXED: merges JSON detail with backend "items" + "missingItems"
  const bindRowsMerged = (jsonDetail, verifiedItems, missingItems) => {
    const byOrd = new Map();
    for (const it of verifiedItems || []) {
      const ord = safeNum(it.ord, 0);
      if (ord > 0 && !byOrd.has(ord)) byOrd.set(ord, it);
    }

    const missingOrd = new Set((missingItems || []).map((m) => safeNum(m.ord, 0)));

    const mismatches = [];
    const out = (jsonDetail || []).map((x, idx) => {
      const ord = idx + 1;
      const vr = byOrd.get(ord);

      const inputItemcode = deriveItemcode(x);
      const inputItemname = deriveItemname(x);

      const isMissing = missingOrd.has(ord);
      const item_found = vr ? !!vr.item_found : !isMissing ? true : false;

      // rack comes from backend items (stock/NA decision)
      const rackid = safeNum(vr?.rackid ?? x.rackid ?? 0, 0);
      const rackname = safeText(vr?.rackname ?? x.rackname ?? "NA");

      if (isMissing || !item_found) {
        mismatches.push({
          itemcode: inputItemcode || "-",
          reason: "Item not found in item master",
        });
      }

      if (safeText(rackname).toUpperCase() === "NA") {
        mismatches.push({
          itemcode: inputItemcode || "-",
          reason: "Rack not found (Company+Branch) → NA used",
        });
      }

      const rowKeyBase = inputItemcode || safeText(x.partno || x.partdetails) || "ROW";

      const baseRow = {
        _rowKey: `${rowKeyBase}__${idx}`,
        itemcode: safeText(vr?.itemcode ?? inputItemcode),
        itemname: safeText(vr?.itemname) || safeText(x.itemname) || inputItemname,

        serial: safeNum(x.serial, idx + 1),
        itemid: safeNum(vr?.itemid ?? x.itemid ?? 0, 0),

        partno: safeText(x.partno || x.partdetails || ""),
        stkqty: safeNum(vr?.stkqty ?? x.stkqty ?? 0, 0),

        rackid,
        rackname,

        saleqty: safeNum(x.saleqty, 0),
        mrp: safeNum(x.mrp, 0),
        price: safeNum(x.price, 0),
        gstpers: safeNum(x.gstpers, 0),

        item_found,
      };

      return recalcRow(baseRow);
    });

    setrows(out);

    // uniq mismatch list
    const uniq = [];
    const seen = new Set();
    for (const m of mismatches) {
      const k = `${m.itemcode}__${m.reason}`;
      if (seen.has(k)) continue;
      seen.add(k);
      uniq.push(m);
    }
    setMismatchItems(uniq);

    if (uniq.length) {
      toast.info(`${uniq.length} issue(s) found. Click (i) for details.`);
    } else {
      toast.success("Verified & bound to screen");
    }
  };

  const handleConfirmYes = async () => {
    try {
      setConfirmOpen(false);
      if (!parsedJson) return;

      const h = parsedJson.header || {};
      const d = Array.isArray(parsedJson.detail) ? parsedJson.detail : [];

      bindHeader(h);

      const vr = await verifyWithBackend(parsedJson);

      // even if backend returns type="error", it may still contain items+missingItems
      bindRowsMerged(d, vr.items, vr.missingItems);

      if (vr.type === "error") {
        toast.error(vr.message || "Some items missing");
        setMismatchOpen(true);
      }
    } catch (e) {
      console.log(e);
      toast.error(e?.message || "Verify failed");
    }
  };

  const handleClearJson = () => {
    setPickedFileName("");
    setParsedJson(null);
    setMismatchItems([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.info("JSON cleared");
  };

  // =========================
  // ✅ VIEW MODE: DOCUMENT AUTOCOMPLETE
  // =========================
  const [docOptions, setDocOptions] = useState([]); // [{docid, docno}]
  const [docLoading, setDocLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docSearchText, setDocSearchText] = useState("");

  const loadDocuments = async () => {
    try {
      setDocLoading(true);
      const r = await fetch("/stk/transfer/documents", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const j = await r.json().catch(() => []);
      if (!r.ok) throw new Error("Failed to load documents");
      const arr = Array.isArray(j) ? j : [];
      setDocOptions(arr);
    } catch (e) {
      console.log(e);
      setDocOptions([]);
      toast.error(e?.message || "Doc list failed");
    } finally {
      setDocLoading(false);
    }
  };

  useEffect(() => {
    if (userToken && isViewMode) loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, isViewMode]);

  const fetchDocById = async (docid) => {
    const r = await fetch(`/stk/transfer/doc/${docid}`, {
      method: "GET",
      headers: { sptoken: userToken },
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j?.type === "error") throw new Error(j?.message || "Failed to load document");

    const headerObj = j.header || j.headerdata || j.headerData || j?.rows?.[0] || {};
    const detailArr = j.detail || j.detaildata || j.detailData || j?.rows || [];
    return { headerObj, detailArr };
  };

  const onSelectDoc = async (newDoc) => {
    try {
      setSelectedDoc(newDoc);
      if (!newDoc?.docid) return;

      const { headerObj, detailArr } = await fetchDocById(newDoc.docid);
      bindHeader(headerObj);

      // view mode: detail already includes rackid/rackname etc.
      bindRowsMerged(detailArr, [], []);
      toast.success(`Loaded ${newDoc.docno}`);
    } catch (e) {
      console.log(e);
      toast.error(e?.message || "Load failed");
    }
  };

  // =========================
  // GRID UPDATE
  // =========================
  const processRowUpdate = (newRow) => {
    const updated = recalcRow(newRow);
    setrows((prev) =>
      prev.map((r) => (String(r._rowKey) === String(updated._rowKey) ? updated : r))
    );
    return updated;
  };

  // =========================
  // UI
  // =========================
  return (
    <div>
      <Sidebar Title={"TEMPORARY STOCK TRANSFER (JSON VERIFY)"} expandedPanel={"panel2"}>
        <Box component="form" sx={{ width: "100%" }} autoComplete="off">
          {/* TOP: JSON Upload + Verify (New mode only) */}
          {!isViewMode && (
            <Stack spacing={1} mb={"15px"} direction="row" justifyContent={"center"} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: "none" }}
                onChange={onPickFile}
              />
              <ColorButton type="button" onClick={() => fileInputRef.current?.click()} startIcon={<UploadFileIcon />}>
                Upload JSON
              </ColorButton>

              <TextField
                label="Selected File"
                value={pickedFileName}
                sx={{ width: 420, ...compactTfSx }}
                size="small"
                inputProps={{ readOnly: true }}
              />

              <ColorButton type="button" onClick={openVerifyConfirm} disabled={!parsedJson} startIcon={<TaskAltIcon />}>
                VERIFY
              </ColorButton>

              <Tooltip title={mismatchItems.length ? "View issues" : "No issues"}>
                <span>
                  <IconButton onClick={() => setMismatchOpen(true)} disabled={!mismatchItems.length} sx={{ ml: 0.5 }}>
                    <Badge badgeContent={mismatchItems.length} color="error">
                      <InfoOutlinedIcon />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>

              <Button type="button" onClick={handleClearJson} disabled={!pickedFileName}>
                Clear
              </Button>

              <ColorButton type="button" onClick={saveTransfer} disabled={saving || !rows.length}>
                {saving ? "Saving..." : "SAVE"}
              </ColorButton>
            </Stack>
          )}

          <hr />

          {/* MODE + DATE + VIEW DOC AUTOCOMPLETE */}
          <Stack spacing={1} columnGap={2} mb={1} direction="row" mt={2} ml={4} alignItems="center">
            <TextField
              id="mode"
              required
              select
              label="Mode"
              value={mode}
              onChange={(e) => setMode(Number(e.target.value))}
              variant="outlined"
              sx={{ width: "16%", ...compactTfSx }}
              size="small"
            >
              {allmodes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={orderdate || null}
                onChange={(newValue) => setorderdate(newValue)}
                renderInput={(params) => (
                  <TextField required {...params} sx={{ width: "16%", ...compactTfSx }} size="small" />
                )}
              />
            </LocalizationProvider>

            <TextField
              label="Ref Doc No"
              value={refdocno}
              onChange={(e) => setRefDocno(e.target.value)}
              sx={{ width: 220, ...compactTfSx }}
              size="small"
              disabled={isViewMode}
            />

            {/* VIEW MODE DOC SELECT */}
            {isViewMode && (
              <Autocomplete
                options={docOptions}
                value={selectedDoc}
                loading={docLoading}
                inputValue={docSearchText}
                onInputChange={(_, v) => setDocSearchText(v)}
                getOptionLabel={(o) => o?.docno || ""}
                isOptionEqualToValue={(o, v) => String(o?.docid) === String(v?.docid)}
                onOpen={() => {
                  if (!docOptions.length && userToken) loadDocuments();
                }}
                onChange={(_, newVal) => onSelectDoc(newVal)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Doc No"
                    size="small"
                    sx={{ width: 260, ...compactTfSx }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {docLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Stack>

          {/* BRANCHES */}
          <Paper elevation={1} sx={{ mx: 4, mt: 1, p: 1, borderRadius: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <BranchCardViewOnly
                  title="FROM"
                  onReset={resetFrom}
                  company={frombranchcompany}
                  branch={frombranchname}
                  addr1={frombranchaddr1}
                  addr2={frombranchaddr2}
                  addr3={frombranchaddr3}
                />
              </Box>

              <Box sx={{ width: "1px", bgcolor: "divider" }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <BranchCardViewOnly
                  title="TO"
                  onReset={resetTo}
                  company={tobranchcompany}
                  branch={tobranchname}
                  addr1={tobranchaddr1}
                  addr2={tobranchaddr2}
                  addr3={tobranchaddr3}
                />
              </Box>
            </Stack>
          </Paper>

          {/* GRID */}
          <Box
            sx={{
              height: 550,
              width: "100%",
              position: "relative",
              mt: "15px",
              boxShadow: "2px 6px 15px #5a5a5a",
              "& .super-app-theme--header": { backgroundColor: "#3bb9ff", color: "#000" },
            }}
          >
            <DataGrid
              apiRef={apiRef}
              sx={{
                border: "1px solid #000",
                "& .MuiDataGrid-cell:focus": {
                  outline: "2px solid red",
                  backgroundColor: "gray",
                  color: "white",
                },
              }}
              rowHeight={25}
              headerHeight={40}
              showColumnRightBorder
              rows={rows}
              columns={columns}
              pageSize={100}
              getRowId={(row) => row._rowKey}
              editMode="cell"
              isCellEditable={(params) => !isViewMode && params.field === "rackname"}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(e) => console.log(e)}
            />

            {/* TOTALS */}
            <Box sx={{ padding: "10px", height: 170 }}>
              <Stack direction="row" mt={2}>
                <Table sx={{ minWidth: "100%", boxShadow: "2px 6px 15px #5a5a5a" }} size="small">
                  <TableHead sx={{ backgroundColor: "#3bb9ff", border: "0.5px solid black" }}>
                    <TableRow>
                      <TableCell sx={{ color: "#000", border: "0.5px solid black" }} align="center">
                        Total Stock Value
                      </TableCell>
                      <TableCell sx={{ color: "#000", border: "0.5px solid black" }} align="center">
                        GST Amt
                      </TableCell>
                      <TableCell sx={{ color: "#000", border: "0.5px solid black" }} align="center">
                        Net Stock Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: "whitesmoke" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ color: "#000", border: "0.5px solid black" }}>
                        {totals.totvalue}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#000", border: "0.5px solid black" }}>
                        {totals.totgstamt}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#000", border: "0.5px solid black" }}>
                        {totals.totnetamt}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Stack>
            </Box>
          </Box>

          {/* CONFIRM DIALOG */}
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Confirm Verify</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will verify items using itemcode and match rack by Company + Branch. If rack not found, rackname
                will be NA.
              </DialogContentText>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2">
                  <b>Doc No:</b> {confirmSummary.docno || "-"}
                </Typography>
                <Typography variant="body2">
                  <b>Items:</b> {confirmSummary.items}
                </Typography>
                <Typography variant="body2">
                  <b>From:</b> {confirmSummary.from || "-"}
                </Typography>
                <Typography variant="body2">
                  <b>To:</b> {confirmSummary.to || "-"}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <ColorButton onClick={handleConfirmYes}>Yes, Verify</ColorButton>
            </DialogActions>
          </Dialog>

          {/* ISSUE DIALOG */}
          <Dialog open={mismatchOpen} onClose={closeMismatch} maxWidth="md" fullWidth>
            <DialogTitle>Issues</DialogTitle>
            <DialogContent dividers>
              {!mismatchItems.length ? (
                <Typography variant="body2">No issues.</Typography>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Items needing attention (missing item master or rack master). You can fix rack from the Rack column.
                  </Typography>

                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
                      <TableRow>
                        <TableCell>
                          <b>Item Code</b>
                        </TableCell>
                        <TableCell>
                          <b>Reason</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mismatchItems.map((m, idx) => (
                        <TableRow key={`${m.itemcode}_${idx}`}>
                          <TableCell>{m.itemcode || "-"}</TableCell>
                          <TableCell>{m.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeMismatch}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Sidebar>
    </div>
  );
};

export default StkTransferByJson;